/**
 * 认证 store：会话用户 + 登录/注册/登出/引导。
 * - accessToken 只存内存（api.ts 模块级），刷新走 httpOnly cookie lc_refresh
 * - setAuthListener 接入 401 自动续签：续签成功更新 user，失败退回 guest
 * - 登出时同步锁定保险箱（清零内存密钥）
 */
import { create } from 'zustand';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  setAccessToken,
  setAuthListener,
} from '../lib/api';
import type { AuthResponse, LoginBody, RegisterBody, SessionUser } from '../lib/types';
import { useVault } from './vault';

export type AuthStatus = 'loading' | 'authed' | 'guest';

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  /** 应用启动时探测会话（cookie 续签后取 /auth/me） */
  bootstrap: () => Promise<void>;
  login: (body: LoginBody) => Promise<SessionUser>;
  register: (body: RegisterBody) => Promise<SessionUser>;
  logout: () => Promise<void>;
  /** 刷新用户资料（vaultInitialized 等字段变化后） */
  refreshUser: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  status: 'loading',
  user: null,

  async bootstrap() {
    try {
      const user = await fetchMe();
      set({ status: 'authed', user });
    } catch {
      setAccessToken(null);
      set({ status: 'guest', user: null });
    }
  },

  async login(body) {
    const res = await apiLogin(body);
    setAccessToken(res.accessToken);
    set({ status: 'authed', user: res.user });
    return res.user;
  },

  async register(body) {
    const res = await apiRegister(body);
    setAccessToken(res.accessToken);
    set({ status: 'authed', user: res.user });
    return res.user;
  },

  async logout() {
    try {
      await apiLogout();
    } catch {
      /* 登出接口失败也照常清理本地会话 */
    } finally {
      setAccessToken(null);
      useVault.getState().lock();
      set({ status: 'guest', user: null });
    }
  },

  async refreshUser() {
    try {
      const user = await fetchMe();
      set({ status: 'authed', user });
    } catch {
      /* 会话失效由请求层 / bootstrap 处理 */
    }
  },
}));

// 401 自动续签时同步会话状态（api.ts 不依赖 store，避免循环依赖）
setAuthListener((res: AuthResponse | null) => {
  if (res) {
    setAccessToken(res.accessToken);
    useAuth.setState({ status: 'authed', user: res.user });
  } else {
    setAccessToken(null);
    useVault.getState().lock();
    useAuth.setState({ status: 'guest', user: null });
  }
});
